import type { BoardState } from '../state/board-state.js';
import type { LineAnchor } from '../interfaces/board-object.interface.js';

/**
 * Describes a single anchor mutation that needs to be applied to a line
 * when one of its referenced targets is removed. The `originalAnchor`
 * field captures the pre-removal anchor verbatim, so a `revert()` can
 * reattach the line precisely to its previous target.
 */
export interface AnchorChange {
  readonly lineId: string;
  readonly endpoint: 'start' | 'end';
  readonly originalAnchor: LineAnchor;
}

/**
 * Single-pass O(n) scan that collects every line whose start- or end-anchor
 * references one of the objects in `targetIds`. Lines that are themselves in
 * `targetIds` are skipped — they will be removed entirely, so anchor
 * patching would be wasted work.
 *
 * The returned Map is keyed by lineId; for a line with both endpoints
 * pointing at targets we emit two separate AnchorChange entries
 * (start first, then end) preserving deterministic ordering for
 * downstream consumers (event-bundling, undo stacks).
 */
export function collectAffectedLines(
  state: BoardState,
  targetIds: ReadonlySet<string>,
): Map<string, AnchorChange[]> {
  const result = new Map<string, AnchorChange[]>();
  for (const obj of state.allObjects()) {
    if (obj.type !== 'line') continue;
    if (targetIds.has(obj.id)) continue;
    const changes: AnchorChange[] = [];
    if (obj.startAnchor && targetIds.has(obj.startAnchor.objectId)) {
      changes.push({ lineId: obj.id, endpoint: 'start', originalAnchor: obj.startAnchor });
    }
    if (obj.endAnchor && targetIds.has(obj.endAnchor.objectId)) {
      changes.push({ lineId: obj.id, endpoint: 'end', originalAnchor: obj.endAnchor });
    }
    if (changes.length > 0) result.set(obj.id, changes);
  }
  return result;
}
